import { X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { webNoOutline } from '@/lib/web-style';
import { webTransition } from '@/lib/hover-style';
import type { Difficulty } from '@/db/types';

interface Props {
  visible: boolean;
  /** Current task values. */
  initial: {
    title: string;
    timeMinutes: number | null;
    difficulty: Difficulty | null;
  };
  onClose: () => void;
  onSave: (next: {
    title: string;
    timeMinutes: number | null;
    difficulty: Difficulty | null;
  }) => Promise<void> | void;
}

/**
 * Inline editor for an existing task. Lets the user override the
 * rule-derived title, time estimate, and difficulty to make a
 * predefined task more specific to their home.
 *
 * Saving marks the task as `is_custom = 1` on the caller side so
 * subsequent Replans don't blow away the user's edits. Reverting to
 * the rule version means deleting the task (the next Replan will
 * recreate from the rule).
 */
export function TaskEditModal({ visible, initial, onClose, onSave }: Props) {
  const [title, setTitle] = useState(initial.title);
  const [timeStr, setTimeStr] = useState(
    initial.timeMinutes != null ? String(initial.timeMinutes) : ''
  );
  const [diff, setDiff] = useState<Difficulty | null>(initial.difficulty);

  // Reseed state every time the modal opens — without this, switching
  // between tasks reuses the previous task's edits.
  useEffect(() => {
    if (!visible) return;
    setTitle(initial.title);
    setTimeStr(initial.timeMinutes != null ? String(initial.timeMinutes) : '');
    setDiff(initial.difficulty);
  }, [visible, initial.title, initial.timeMinutes, initial.difficulty]);

  const handleSave = async () => {
    const t = title.trim();
    if (!t) return;
    const parsed = Number(timeStr);
    const timeMinutes =
      timeStr.trim() === '' || Number.isNaN(parsed) ? null : Math.max(1, Math.round(parsed));
    await onSave({ title: t, timeMinutes, difficulty: diff });
    onClose();
  };

  return (
    <Modal animationType="fade" transparent visible={visible} onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          accessibilityLabel="Close editor"
          style={StyleSheet.absoluteFill}
          onPress={onClose}
        />
        <View style={styles.card} onStartShouldSetResponder={() => true}>
          <View style={styles.header}>
            <Text style={styles.title}>Edit task</Text>
            <Pressable
              accessibilityLabel="Close"
              onPress={onClose}
              hitSlop={8}
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={16} color={Palette.inkSoft} strokeWidth={1.8} />
            </Pressable>
          </View>

          <View style={styles.body}>
            <View style={styles.field}>
              <Text style={styles.label}>Title</Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="What's this task?"
                placeholderTextColor={Palette.inkFaint}
                style={[styles.input, webNoOutline]}
                maxLength={120}
              />
            </View>

            <View style={styles.row}>
              <View style={[styles.field, { flex: 1 }]}>
                <Text style={styles.label}>Time (minutes)</Text>
                <TextInput
                  value={timeStr}
                  onChangeText={(v) => setTimeStr(v.replace(/[^0-9]/g, ''))}
                  placeholder="10"
                  placeholderTextColor={Palette.inkFaint}
                  keyboardType="number-pad"
                  style={[styles.input, webNoOutline]}
                  maxLength={4}
                />
              </View>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>Difficulty</Text>
              <View style={styles.diffRow}>
                {(['easy', 'moderate', 'hard'] as const).map((d) => (
                  <Pressable
                    key={d}
                    onPress={() => setDiff(d)}
                    style={({ pressed }) => [
                      styles.diffChip,
                      diff === d && styles.diffChipActive,
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Text
                      style={[
                        styles.diffChipText,
                        diff === d && styles.diffChipTextActive,
                      ]}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </Text>
                  </Pressable>
                ))}
              </View>
              <Text style={styles.hint}>
                Your edits stay even after a Replan — Homestead treats edited
                tasks as your own. Delete the task to get the original back.
              </Text>
            </View>
          </View>

          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              style={({ pressed }) => [styles.cancelBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={handleSave}
              disabled={title.trim().length === 0}
              style={({ pressed }) => [
                styles.saveBtn,
                webTransition,
                title.trim().length === 0 && { opacity: 0.4 },
                pressed && { transform: [{ scale: 0.96 }] },
              ]}>
              <Text style={styles.saveBtnText}>Save changes</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.three,
  },
  card: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    width: '100%',
    maxWidth: 480,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: Spacing.four,
    paddingRight: Spacing.three,
    paddingVertical: Spacing.three,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: Spacing.four,
    gap: Spacing.three,
    backgroundColor: Palette.card,
  },
  row: { flexDirection: 'row', gap: Spacing.three },
  field: { gap: 6 },
  label: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkSoft,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  input: {
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  diffRow: {
    flexDirection: 'row',
    gap: 6,
  },
  diffChip: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: Palette.sand,
    borderWidth: 1.5,
    borderColor: Palette.line,
    alignItems: 'center',
  },
  diffChipActive: {
    backgroundColor: '#EAF0EA',
    borderColor: Palette.forest,
  },
  diffChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  diffChipTextActive: { color: Palette.forest },
  hint: {
    marginTop: 4,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkFaint,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    gap: Spacing.three,
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.three,
    justifyContent: 'flex-end',
    borderTopWidth: 1,
    borderTopColor: Palette.line,
  },
  cancelBtn: {
    paddingHorizontal: Spacing.four,
    paddingVertical: 10,
    borderRadius: 999,
  },
  cancelBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  saveBtn: {
    backgroundColor: Palette.forest,
    paddingHorizontal: Spacing.five,
    paddingVertical: 10,
    borderRadius: 999,
  },
  saveBtnText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    letterSpacing: 0.2,
  },
});
